/**
 * decalManager.js - Triplanar Decal System
 *
 * Handles all decal operations including:
 * - Triplanar projection shader materials
 * - Decal creation, update, and removal
 * - Decal overlay management (viewport buttons)
 * - Group and side decals
 */

import * as THREE from 'three';
import * as state from './state.js';
import {
    renderer,
    camera,
    scene,
    sideMeshes,
    originalMaterials,
    graphicLayers,
    graphicLayerMap,
    uploadedGraphics,
    groupDecalSettings,
    decalSettings,
    lockedGroupDecals,
    decalMaterialsByGroup,
    groupDecals,
    sideDecals,
    lockedDecals,
    initializedMeshes,
    viewportButtonsMap,
    globalDummyTexture,
    setGlobalDummyTexture,
    pendingGroupTexture,
    activeGroup,
    activeSide,
    activeSideKey,
    setPendingGroupTexture,
    setActiveGroup,
    containerEl
} from './state.js';
import { requestRender } from './sceneManager.js';
import { getGlobals, allowsDecals } from './utils.js';

const GLOBALS = getGlobals();

// ===== DUMMY TEXTURE =====
export function getDummyTexture() {
    if (!globalDummyTexture) {
        const dummyCanvas = document.createElement('canvas');
        dummyCanvas.width = dummyCanvas.height = 1;
        setGlobalDummyTexture(new THREE.CanvasTexture(dummyCanvas));
    }
    return globalDummyTexture;
}

// ===== HELPER: UV ZU 3D POSITION (MIT FALLBACK AUF ALLE MESHES) =====
function uvToWorldPosition(mesh, targetUV, allMeshes = null) {
    // Versuche zuerst das angegebene Mesh
    const result = uvToWorldPositionOnMesh(mesh, targetUV);
    if (result) return result;

    // Fallback: Suche in allen verfügbaren Meshes
    if (allMeshes) {
        for (const otherMesh of allMeshes) {
            if (otherMesh === mesh) continue;
            const result = uvToWorldPositionOnMesh(otherMesh, targetUV);
            if (result) return result;
        }
    }

    return null;
}

function uvToWorldPositionOnMesh(mesh, targetUV) {
    if (!mesh || !mesh.geometry) return null;

    const geometry = mesh.geometry;
    const position = geometry.attributes.position;
    const uv = geometry.attributes.uv;

    if (!position || !uv) return null;

    const index = geometry.index;
    const faceCount = index ? index.count / 3 : position.count / 3;

    // Finde das Dreieck, das diese UV-Koordinate enthält
    for (let i = 0; i < faceCount; i++) {
        const i0 = index ? index.getX(i * 3) : i * 3;
        const i1 = index ? index.getX(i * 3 + 1) : i * 3 + 1;
        const i2 = index ? index.getX(i * 3 + 2) : i * 3 + 2;

        const uv0 = new THREE.Vector2(uv.getX(i0), uv.getY(i0));
        const uv1 = new THREE.Vector2(uv.getX(i1), uv.getY(i1));
        const uv2 = new THREE.Vector2(uv.getX(i2), uv.getY(i2));

        // Baryzentrische Koordinaten
        const v0 = new THREE.Vector2().subVectors(uv2, uv0);
        const v1 = new THREE.Vector2().subVectors(uv1, uv0);
        const v2 = new THREE.Vector2().subVectors(targetUV, uv0);

        const dot00 = v0.dot(v0);
        const dot01 = v0.dot(v1);
        const dot02 = v0.dot(v2);
        const dot11 = v1.dot(v1);
        const dot12 = v1.dot(v2);

        const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
        const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
        const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

        // Prüfe ob Punkt im Dreieck liegt (mit kleiner Toleranz)
        if (u >= -0.01 && v >= -0.01 && (u + v) <= 1.01) {
            // Interpoliere 3D-Position
            const p0 = new THREE.Vector3().fromBufferAttribute(position, i0);
            const p1 = new THREE.Vector3().fromBufferAttribute(position, i1);
            const p2 = new THREE.Vector3().fromBufferAttribute(position, i2);

            const point = new THREE.Vector3()
                .addScaledVector(p0, 1 - u - v)
                .addScaledVector(p1, v)
                .addScaledVector(p2, u);

            point.applyMatrix4(mesh.matrixWorld);
            return point;
        }
    }

    return null;
}

// ===== HELPER: DECAL-RAHMEN-PUNKTE BERECHNEN (MIT ZWISCHENPUNKTEN) =====

// Cache für Border-Punkte pro Grafik-ID
const borderPointsCache = new Map();

// Funktion zum Invalidieren des Border-Punkte-Caches
export function invalidateBorderPointsCache(graphicId = null) {
    if (graphicId) {
        borderPointsCache.delete(graphicId);
    } else {
        borderPointsCache.clear();
    }
}

function getDecalBorderPoints(mesh, centerUV, sizeUV, rotation, allMeshes = null, graphicId = null) {
    if (!mesh) return null;

    // Prüfe Cache wenn graphicId vorhanden
    if (graphicId) {
        const cached = borderPointsCache.get(graphicId);
        if (cached && 
            cached.centerUV.x === centerUV.x && 
            cached.centerUV.y === centerUV.y &&
            cached.sizeUV.x === sizeUV.x &&
            cached.sizeUV.y === sizeUV.y &&
            cached.rotation === rotation &&
            cached.meshUuid === mesh.uuid) {
            return cached.points;
        }
    }

    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const halfWidth = sizeUV.x;
    const halfHeight = sizeUV.y;

    // 4 Ecken im lokalen Raum
    const localCorners = [
        [-halfWidth, -halfHeight],
        [halfWidth, -halfHeight],
        [halfWidth, halfHeight],
        [-halfWidth, halfHeight]
    ];

    const allPoints = [];
    const segmentsPerEdge = 6; // Reduziert von 10 auf 6 für bessere Performance

    // Für jede Kante: Erstelle Zwischenpunkte
    for (let i = 0; i < 4; i++) {
        const corner1 = localCorners[i];
        const corner2 = localCorners[(i + 1) % 4];

        // Interpoliere entlang der Kante
        for (let j = 0; j < segmentsPerEdge; j++) {
            const t = j / segmentsPerEdge;
            const x = corner1[0] * (1 - t) + corner2[0] * t;
            const y = corner1[1] * (1 - t) + corner2[1] * t;

            // Rotiere
            const rotatedX = x * cos - y * sin;
            const rotatedY = x * sin + y * cos;

            // Transformiere zu UV
            const uv = new THREE.Vector2(
                centerUV.x + rotatedX,
                centerUV.y + rotatedY
            );

            // Konvertiere zu 3D
            const point3D = uvToWorldPosition(mesh, uv, allMeshes);
            if (point3D) {
                allPoints.push(point3D);
            }
        }
    }

    const result = allPoints.length > 0 ? allPoints : null;

    // Speichere im Cache wenn graphicId vorhanden
    if (graphicId && result) {
        borderPointsCache.set(graphicId, {
            centerUV: { x: centerUV.x, y: centerUV.y },
            sizeUV: { x: sizeUV.x, y: sizeUV.y },
            rotation: rotation,
            meshUuid: mesh.uuid,
            points: result
        });
    }

    return result;
}

// ===== DECAL OVERLAY MANAGEMENT =====

// Cache für alle Meshes - wird nur einmal pro Model-Load erstellt
let cachedAllMeshes = null;
let cachedAllMeshesVersion = 0;

// Funktion zum Invalidieren des Mesh-Caches (bei Model-Reload aufrufen)
export function invalidateMeshCache() {
    cachedAllMeshes = null;
    cachedAllMeshesVersion++;
    // Auch Border-Punkte-Cache invalidieren
    borderPointsCache.clear();
}

// Funktion zum Abrufen aller Meshes (gecacht)
function getAllMeshesCached() {
    if (cachedAllMeshes === null) {
        cachedAllMeshes = [];
        sideMeshes.forEach(groupSides => {
            groupSides.forEach(meshSet => {
                meshSet.forEach(mesh => cachedAllMeshes.push(mesh));
            });
        });
    }
    return cachedAllMeshes;
}

// Funktion zum Finden des nächsten Meshes zu einer Position (mit Caching)
function findClosestMeshToPosition(worldPosition, allMeshes) {
    let hitMesh = null;
    let minDist = Infinity;
    
    // Reuse Vector3 für Performance
    const closestPoint = new THREE.Vector3();
    
    for (const mesh of allMeshes) {
        // Nutze gecachte Bounding Box wenn verfügbar
        if (!mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
        }
        
        // Transformiere Bounding Box in World Space
        const bbox = new THREE.Box3().copy(mesh.geometry.boundingBox);
        bbox.applyMatrix4(mesh.matrixWorld);
        
        bbox.clampPoint(worldPosition, closestPoint);
        const dist = closestPoint.distanceTo(worldPosition);
        
        if (dist < minDist) {
            minDist = dist;
            hitMesh = mesh;
        }
    }
    
    return hitMesh;
}

export function updateDecalOverlays() {
    if (!containerEl) {
        return;
    }
    const overlayContainer = containerEl.querySelector('.decal-overlay-container');
    if (!overlayContainer) {
        return;
    }

    // Track which graphics are currently being edited
    const activeGraphicIds = new Set();
    
    // Hole gecachte Mesh-Liste einmal für alle Grafiken
    const allMeshes = getAllMeshesCached();

    uploadedGraphics.forEach(graphic => {
        const layerIndex = graphicLayerMap.get(graphic.id);

        if (layerIndex === undefined) {
            return;
        }

        const layer = graphicLayers[layerIndex];

        if (!layer) {
            return;
        }

        if (!graphic.isEditing) {
            return;
        }

        if (!layer.worldPosition) {
            return;
        }

        activeGraphicIds.add(graphic.id);

        const pos = layer.worldPosition.clone();
        const projected = pos.clone().project(camera);

        const canvas = renderer.domElement;
        const screenX = (projected.x * 0.5 + 0.5) * canvas.clientWidth;
        const screenY = (-(projected.y) * 0.5 + 0.5) * canvas.clientHeight;

        if (isNaN(screenX) || isNaN(screenY)) {
            return;
        }

        const size = layer.sizeUV;

        // Berechne perspektivisch korrekte Größe basierend auf Kamera-Distanz
        const distanceToCamera = camera.position.distanceTo(pos);

        // Projiziere einen Referenzpunkt um die Skalierung zu berechnen
        const cameraDir = new THREE.Vector3();
        camera.getWorldDirection(cameraDir);
        const right = new THREE.Vector3();
        right.crossVectors(camera.up, cameraDir).normalize();

        // Erstelle einen Punkt, der 1 Einheit rechts vom Zentrum ist
        const refPoint = pos.clone().add(right.clone().multiplyScalar(1));
        const refProjected = refPoint.project(camera);
        const refScreenX = (refProjected.x * 0.5 + 0.5) * canvas.clientWidth;

        // Berechne Pixel pro Welteinheit
        const pixelsPerUnit = Math.abs(refScreenX - screenX);

        // Skaliere die UV-Größe basierend auf der Projektion
        // UV-Größe 0.2 entspricht ungefähr 20% der Textur, was wir als 2 Einheiten annehmen
        const worldScale = 5; // Anpassungsfaktor (reduziert für kleineren Rahmen)
        const pxW = size.x * pixelsPerUnit * worldScale;
        const pxH = size.y * pixelsPerUnit * worldScale;

        const topLeft = {x: screenX - pxW / 2, y: screenY - pxH / 2};
        const topRight = {x: screenX + pxW / 2, y: screenY - pxH / 2};

        // Erstelle oder aktualisiere 3D-Rahmen auf der UV-Map
        const frameKey = `decal-frame-${graphic.id}`;
        let frame3D = scene.getObjectByName(frameKey);

        if (!frame3D) {
            // Erstelle gestrichelte Linie für den Rahmen
            const geometry = new THREE.BufferGeometry();
            const material = new THREE.LineDashedMaterial({
                color: 0xFFDE00, // Primary-Farbe der Seite (Gelb)
                dashSize: 0.05,
                gapSize: 0.025,
                linewidth: 6,
                opacity: 1.0,
                transparent: false,
                depthTest: true, // Respektiere Tiefe - nicht durch Modell sichtbar
                depthWrite: false
            });

            frame3D = new THREE.LineSegments(geometry, material);
            frame3D.name = frameKey;
            frame3D.renderOrder = 9999; // Sehr hoher renderOrder
            scene.add(frame3D);
        }

        // Berechne die 4 Ecken des Decals auf der UV-Map
        // Wenn hitMesh fehlt (z.B. bei geladenen Konfigurationen), versuche es zu finden
        let hitMesh = layer.hitMesh;
        if (!hitMesh && layer.worldPosition) {
            // Nutze optimierte Funktion mit gecachten Bounding Boxes
            hitMesh = findClosestMeshToPosition(layer.worldPosition, allMeshes);

            // Setze hitMesh im Layer für zukünftige Verwendung
            if (hitMesh) {
                layer.hitMesh = hitMesh;
            }
        }

        if (hitMesh) {
            const borderPoints = getDecalBorderPoints(
                hitMesh,
                layer.centerUV,
                layer.sizeUV,
                layer.rotation || 0,
                allMeshes,
                graphic.id
            );

            if (borderPoints && borderPoints.length >= 3) {
                // Verschiebe Punkte leicht nach außen für Sichtbarkeit
                const validPoints = borderPoints.map(point => {
                    const offset = point.clone().sub(pos).normalize().multiplyScalar(0.001);
                    return point.clone().add(offset);
                });

                // Erstelle Segmente nur zwischen nahen Punkten (filtere große Sprünge)
                const maxDistance = 0.3; // Maximale Distanz für gültige Verbindungen
                const segmentPositions = [];

                for (let i = 0; i < validPoints.length - 1; i++) {
                    const dist = validPoints[i].distanceTo(validPoints[i + 1]);
                    if (dist < maxDistance) {
                        // Gültige Verbindung - füge beide Punkte hinzu
                        segmentPositions.push(
                            validPoints[i].x, validPoints[i].y, validPoints[i].z,
                            validPoints[i + 1].x, validPoints[i + 1].y, validPoints[i + 1].z
                        );
                    }
                }

                // Schließe den Loop wenn möglich
                const firstToLast = validPoints[0].distanceTo(validPoints[validPoints.length - 1]);
                if (firstToLast < maxDistance) {
                    segmentPositions.push(
                        validPoints[validPoints.length - 1].x, validPoints[validPoints.length - 1].y, validPoints[validPoints.length - 1].z,
                        validPoints[0].x, validPoints[0].y, validPoints[0].z
                    );
                }

                if (segmentPositions.length >= 6) { // Mindestens ein Segment
                    const positions = new Float32Array(segmentPositions);
                    frame3D.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
                    frame3D.geometry.attributes.position.needsUpdate = true;
                    frame3D.computeLineDistances();
                    frame3D.visible = true;
                } else {
                    frame3D.visible = false;
                }
            } else {
                frame3D.visible = false;
            }
        } else {
            frame3D.visible = false;
        }
    });

    scene.children.slice().forEach(child => {
        if (child.name && child.name.startsWith('decal-frame-')) {
            const graphicId = child.name.replace('decal-frame-', '');
            if (!activeGraphicIds.has(graphicId)) {
                scene.remove(child);
                child.geometry.dispose();
                child.material.dispose();
            }
        }
    });

    // Trigger Render für 3D-Rahmen
    if (activeGraphicIds.size > 0) {
        requestRender();
    }
}

// ===== GRAPHICS LAYER MATERIAL UPDATE =====
export function updateAllMaterialsWithGraphicLayers() {
    // Performance: Nur initialisieren wenn wirklich ein Layer aktiv ist
    const hasActiveLayer = graphicLayers.some(layer => layer !== null);

    // Wenn keine aktiven Layer vorhanden sind und keine Meshes initialisiert wurden, früh zurückkehren
    if (!hasActiveLayer && initializedMeshes.size === 0) {
        return;
    }

    // Iteriere durch alle Meshes und aktualisiere ihre Materialien mit den neuen Grafik-Layers
    sideMeshes.forEach((groupSides, groupName) => {
        groupSides.forEach((meshSet, sideName) => {
            meshSet.forEach(mesh => {
                // Skip protected meshes (_noDecals or _protected)
                if (!allowsDecals(mesh)) return;

                const currentMat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;

                // Performance: Mesh nur einmal initialisieren
                if (!initializedMeshes.has(mesh.uuid)) {
                    // Nur initialisieren wenn es aktive Layer gibt
                    if (!hasActiveLayer) return;

                    // Verwende aktuelles Material (mit Glas-Anpassungen etc.) statt Original
                    const baseMat = currentMat;

                    // Erstelle neues Material mit Grafik-Layer-Support
                    const projectionData = {
                        center: new THREE.Vector3(),
                        size: new THREE.Vector3(1, 1, 1),
                        rotation: 0,
                        scale: 1,
                        normal: new THREE.Vector3(0, 1, 0),
                        isGroupDecal: false,
                        centerUV: new THREE.Vector2(0.5, 0.5),
                        sizeUV: new THREE.Vector2(0.5, 0.5)
                    };

                    if (Array.isArray(mesh.material)) {
                        mesh.material = mesh.material.map(mat =>
                            createTriplanarDecalMaterial(baseMat, getDummyTexture(), projectionData, null, graphicLayers, groupName, sideName)
                        );
                    } else {
                        mesh.material = createTriplanarDecalMaterial(baseMat, getDummyTexture(), projectionData, null, graphicLayers, groupName, sideName);
                    }

                    initializedMeshes.add(mesh.uuid);
                } else {
                    // Aktualisiere bestehende Grafik-Layer Uniforms
                    if (!currentMat.userData.decalUniforms) {
                        return;
                    }

                    for (let i = 0; i < 8; i++) {
                        const layer = graphicLayers[i];

                        // Ermittle isEditing aus uploadedGraphics basierend auf graphicId
                        let isEditing = false;
                        if (layer && layer.graphicId) {
                            const graphic = uploadedGraphics.find(g => g.id === layer.graphicId);
                            isEditing = graphic?.isEditing || false;
                        }

                        currentMat.userData.decalUniforms[`graphicLayer${i}Active`].value = layer ? 1.0 : 0.0;
                        currentMat.userData.decalUniforms[`graphicLayer${i}Map`].value = layer?.texture || null;
                        currentMat.userData.decalUniforms[`graphicLayer${i}CenterUV`].value = layer?.centerUV || new THREE.Vector2(0.5, 0.5);
                        currentMat.userData.decalUniforms[`graphicLayer${i}SizeUV`].value = layer?.sizeUV || new THREE.Vector2(0.5, 0.5);
                        currentMat.userData.decalUniforms[`graphicLayer${i}Rotation`].value = layer?.rotation || 0;
                        currentMat.userData.decalUniforms[`graphicLayer${i}Editing`].value = isEditing ? 1.0 : 0.0;

                        // Auch Shader-Uniforms aktualisieren wenn vorhanden
                        if (currentMat.userData.shader) {
                            currentMat.userData.shader.uniforms[`graphicLayer${i}Active`].value = layer ? 1.0 : 0.0;
                            currentMat.userData.shader.uniforms[`graphicLayer${i}Map`].value = layer?.texture || null;
                            currentMat.userData.shader.uniforms[`graphicLayer${i}CenterUV`].value = layer?.centerUV || new THREE.Vector2(0.5, 0.5);
                            currentMat.userData.shader.uniforms[`graphicLayer${i}SizeUV`].value = layer?.sizeUV || new THREE.Vector2(0.5, 0.5);
                            currentMat.userData.shader.uniforms[`graphicLayer${i}Rotation`].value = layer?.rotation || 0;
                            currentMat.userData.shader.uniforms[`graphicLayer${i}Editing`].value = isEditing ? 1.0 : 0.0;
                        }
                    }

                    // Material update triggern
                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach(mat => {
                            if (mat) mat.needsUpdate = true;
                        });
                    } else {
                        mesh.material.needsUpdate = true;
                    }
                }
            });
        });
    });
}

// ===== TRIPLANAR DECAL MATERIAL CREATION =====
export function createTriplanarDecalMaterial(baseMaterial, decalTexture, projectionData, sideDecalData = null, graphicLayers = [], groupName = null, sideName = null) {
    const {center, size, rotation, scale, normal, isGroupDecal, centerUV, sizeUV} = projectionData;

    const material = baseMaterial.clone();

    // Glas-Material erkennen (für CMS-Setting "Decals auf Glas voll deckend").
    // Nur wenn das Material Glas ist UND das CMS-Setting aktiv ist, werden Decals opak gerendert.
    const isGlassMaterial = !!baseMaterial.name && baseMaterial.name.toLowerCase().includes('glas');
    const opaqueDecalOnGlass = isGlassMaterial && state.glassDecalsOpaque;

    // Explizit Farbe und Material-Eigenschaften vom baseMaterial übernehmen
    if (baseMaterial.color) {
        material.color.copy(baseMaterial.color);
    }

    // WICHTIG: Stelle sicher, dass die aktuelle Gruppen-/Seiten-Farbe angewendet wird
    if (groupName && sideName) {
        const groups = window._configGroups;
        if (groups && groups.has(groupName)) {
            const groupData = groups.get(groupName);
            const sideData = groupData.sides.get(sideName);

            // Seiten-Farbe hat Vorrang vor Gruppen-Farbe
            let targetColor = null;
            let isCamo = false;

            if (sideData && sideData.color) {
                if (sideData.color === 'camo') {
                    isCamo = true;
                } else {
                    targetColor = sideData.color;
                }
            } else if (groupData.color) {
                if (groupData.color === 'camo') {
                    isCamo = true;
                } else {
                    targetColor = groupData.color;
                }
            }

            if (isCamo) {
                material.color.set(0xffffff);
                if (material.emissive) {
                    material.emissive.set(0x000000);
                }
                material.metalness = 0.1;
                material.roughness = 0.9;
                // Verwende gecachte Camo-Textur, falls vorhanden
                if (state.cachedCamoTexture) {
                    material.map = state.cachedCamoTexture;
                } else if (baseMaterial.map) {
                    material.map = baseMaterial.map;
                }
            } else if (targetColor) {
                // Farbe setzen und Textur entfernen (wichtig für Camo -> Farbe Wechsel)
                material.color.set(targetColor);
                material.map = null;
            } else {
                // Kein Camo, keine Farbe -> Basis-Map übernehmen falls vorhanden
                if (baseMaterial.map) {
                    material.map = baseMaterial.map;
                }
            }
        }
    } else {
        // Keine Gruppen/Seiten-Info -> Basis-Map übernehmen falls vorhanden
        if (baseMaterial.map) {
            material.map = baseMaterial.map;
        }
    }
    if (baseMaterial.emissive) {
        material.emissive.copy(baseMaterial.emissive);
    }
    material.metalness = baseMaterial.metalness;
    material.roughness = baseMaterial.roughness;

    // Preserve glass roughness adjustments
    if (baseMaterial.userData && baseMaterial.userData.originalRoughness !== undefined) {
        material.userData.originalRoughness = baseMaterial.userData.originalRoughness;
    }

    if (baseMaterial.userData && baseMaterial.userData.hasTextureOverride !== undefined) {
        material.userData.hasTextureOverride = baseMaterial.userData.hasTextureOverride;
    }

    // Erstelle 8 Grafik-Layer Uniforms
    const MAX_GRAPHIC_LAYERS = 8;
    const graphicLayerUniforms = {};

    for (let i = 0; i < MAX_GRAPHIC_LAYERS; i++) {
        const layer = graphicLayers[i];
        graphicLayerUniforms[`graphicLayer${i}Active`] = {value: layer ? 1.0 : 0.0};
        graphicLayerUniforms[`graphicLayer${i}Map`] = {value: layer?.texture || null};
        graphicLayerUniforms[`graphicLayer${i}CenterUV`] = {value: layer?.centerUV || new THREE.Vector2(0.5, 0.5)};
        graphicLayerUniforms[`graphicLayer${i}SizeUV`] = {value: layer?.sizeUV || new THREE.Vector2(0.5, 0.5)};
        graphicLayerUniforms[`graphicLayer${i}Rotation`] = {value: layer?.rotation || 0};
        graphicLayerUniforms[`graphicLayer${i}Editing`] = {value: layer?.isEditing ? 1.0 : 0.0};
    }

    material.userData.decalUniforms = {
        decalMap: {value: decalTexture},
        decalCenter: {value: center || new THREE.Vector3()},
        decalSize: {value: size || new THREE.Vector3(1, 1, 1)},
        decalRotation: {value: rotation},
        decalScale: {value: scale},
        decalNormal: {value: normal || new THREE.Vector3(0, 0, 1)},
        isGroupDecal: {value: isGroupDecal ? 1.0 : 0.0},
        decalCenterUV: {value: centerUV || new THREE.Vector2(0.5, 0.5)},
        decalSizeUV: {value: sizeUV || new THREE.Vector2(0.5, 0.5)},
        uOpaqueDecal: {value: opaqueDecalOnGlass ? 1.0 : 0.0},

        hasSideDecal: {value: sideDecalData ? 1.0 : 0.0},
        sideDecalMap: {value: sideDecalData?.texture || null},
        sideDecalCenter: {value: sideDecalData?.center || new THREE.Vector3()},
        sideDecalSize: {value: sideDecalData?.size || new THREE.Vector3(1, 1, 1)},
        sideDecalRotation: {value: sideDecalData?.rotation || 0},
        sideDecalNormal: {value: sideDecalData?.normal || new THREE.Vector3(0, 0, 1)},

        ...graphicLayerUniforms
    };

    material.onBeforeCompile = (shader) => {
        shader.uniforms.decalMap = material.userData.decalUniforms.decalMap;
        shader.uniforms.decalCenter = material.userData.decalUniforms.decalCenter;
        shader.uniforms.decalSize = material.userData.decalUniforms.decalSize;
        shader.uniforms.decalRotation = material.userData.decalUniforms.decalRotation;
        shader.uniforms.decalScale = material.userData.decalUniforms.decalScale;
        shader.uniforms.decalNormal = material.userData.decalUniforms.decalNormal;
        shader.uniforms.isGroupDecal = material.userData.decalUniforms.isGroupDecal;
        shader.uniforms.decalCenterUV = material.userData.decalUniforms.decalCenterUV;
        shader.uniforms.decalSizeUV = material.userData.decalUniforms.decalSizeUV;
        shader.uniforms.uOpaqueDecal = material.userData.decalUniforms.uOpaqueDecal;

        shader.uniforms.hasSideDecal = material.userData.decalUniforms.hasSideDecal;
        shader.uniforms.sideDecalMap = material.userData.decalUniforms.sideDecalMap;
        shader.uniforms.sideDecalCenter = material.userData.decalUniforms.sideDecalCenter;
        shader.uniforms.sideDecalSize = material.userData.decalUniforms.sideDecalSize;
        shader.uniforms.sideDecalRotation = material.userData.decalUniforms.sideDecalRotation;
        shader.uniforms.sideDecalNormal = material.userData.decalUniforms.sideDecalNormal;

        // Grafik-Layer Uniforms registrieren
        for (let i = 0; i < 8; i++) {
            shader.uniforms[`graphicLayer${i}Active`] = material.userData.decalUniforms[`graphicLayer${i}Active`];
            shader.uniforms[`graphicLayer${i}Map`] = material.userData.decalUniforms[`graphicLayer${i}Map`];
            shader.uniforms[`graphicLayer${i}CenterUV`] = material.userData.decalUniforms[`graphicLayer${i}CenterUV`];
            shader.uniforms[`graphicLayer${i}SizeUV`] = material.userData.decalUniforms[`graphicLayer${i}SizeUV`];
            shader.uniforms[`graphicLayer${i}Rotation`] = material.userData.decalUniforms[`graphicLayer${i}Rotation`];
            shader.uniforms[`graphicLayer${i}Editing`] = material.userData.decalUniforms[`graphicLayer${i}Editing`];
        }

        material.userData.shader = shader;

        shader.vertexShader = shader.vertexShader.replace(
            '#include <common>',
            `
                #include <common>
                varying vec3 vWorldPosition;
                varying vec3 vWorldNormal;
                varying vec2 vMeshUV;
                `
        );

        shader.vertexShader = shader.vertexShader.replace(
            '#include <worldpos_vertex>',
            `
                #include <worldpos_vertex>
                vWorldPosition = worldPosition.xyz;
                vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
                vMeshUV = uv;
                `
        );

        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <common>',
            `
                #include <common>
                uniform sampler2D decalMap;
                uniform vec3 decalCenter;
                uniform vec3 decalSize;
                uniform float decalRotation;
                uniform float decalScale;
                uniform vec3 decalNormal;
                uniform float isGroupDecal;
                uniform vec2 decalCenterUV;
                uniform vec2 decalSizeUV;
                uniform float uOpaqueDecal;

                uniform float hasSideDecal;
                uniform sampler2D sideDecalMap;
                uniform vec3 sideDecalCenter;
                uniform vec3 sideDecalSize;
                uniform float sideDecalRotation;
                uniform vec3 sideDecalNormal;

                // 8 Grafik-Layer Uniforms
                uniform float graphicLayer0Active;
                uniform sampler2D graphicLayer0Map;
                uniform vec2 graphicLayer0CenterUV;
                uniform vec2 graphicLayer0SizeUV;
                uniform float graphicLayer0Rotation;
                uniform float graphicLayer0Editing;

                uniform float graphicLayer1Active;
                uniform sampler2D graphicLayer1Map;
                uniform vec2 graphicLayer1CenterUV;
                uniform vec2 graphicLayer1SizeUV;
                uniform float graphicLayer1Rotation;
                uniform float graphicLayer1Editing;

                uniform float graphicLayer2Active;
                uniform sampler2D graphicLayer2Map;
                uniform vec2 graphicLayer2CenterUV;
                uniform vec2 graphicLayer2SizeUV;
                uniform float graphicLayer2Rotation;
                uniform float graphicLayer2Editing;

                uniform float graphicLayer3Active;
                uniform sampler2D graphicLayer3Map;
                uniform vec2 graphicLayer3CenterUV;
                uniform vec2 graphicLayer3SizeUV;
                uniform float graphicLayer3Rotation;
                uniform float graphicLayer3Editing;

                uniform float graphicLayer4Active;
                uniform sampler2D graphicLayer4Map;
                uniform vec2 graphicLayer4CenterUV;
                uniform vec2 graphicLayer4SizeUV;
                uniform float graphicLayer4Rotation;
                uniform float graphicLayer4Editing;

                uniform float graphicLayer5Active;
                uniform sampler2D graphicLayer5Map;
                uniform vec2 graphicLayer5CenterUV;
                uniform vec2 graphicLayer5SizeUV;
                uniform float graphicLayer5Rotation;
                uniform float graphicLayer5Editing;

                uniform float graphicLayer6Active;
                uniform sampler2D graphicLayer6Map;
                uniform vec2 graphicLayer6CenterUV;
                uniform vec2 graphicLayer6SizeUV;
                uniform float graphicLayer6Rotation;
                uniform float graphicLayer6Editing;

                uniform float graphicLayer7Active;
                uniform sampler2D graphicLayer7Map;
                uniform vec2 graphicLayer7CenterUV;
                uniform vec2 graphicLayer7SizeUV;
                uniform float graphicLayer7Rotation;
                uniform float graphicLayer7Editing;

                varying vec3 vWorldPosition;
                varying vec3 vWorldNormal;
                varying vec2 vMeshUV;

                mat2 rotate2D(float angle) {
                    float s = sin(angle);
                    float c = cos(angle);
                    return mat2(c, -s, s, c);
                }

                vec4 planarProjection(sampler2D tex, vec3 worldPos, vec3 center, vec3 size, vec3 projNormal, float rot) {
                    vec3 relPos = worldPos - center;

                    vec3 up = abs(projNormal.y) < 0.999 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
                    vec3 tangent = normalize(cross(up, projNormal));
                    vec3 bitangent = cross(projNormal, tangent);

                    vec2 uv = vec2(
                        dot(relPos, tangent) / size.x,
                        dot(relPos, bitangent) / size.y
                    );

                    uv = rotate2D(rot) * uv;

                    uv = uv * 0.5 + 0.5;

                    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
                        return vec4(0.0, 0.0, 0.0, 0.0);
                    }

                    vec4 color = texture2D(tex, uv);

                    return color;
                }

                vec4 uvBasedDecalProjection(sampler2D tex, vec2 meshUV, vec2 decalCenterUV, vec2 decalSize, float rot) {
                    vec2 relativeUV = meshUV - decalCenterUV;

                    // Rotiere in die richtige Richtung (+rot statt -rot)
                    vec2 localUV = rotate2D(rot) * relativeUV;

                    // Teile durch decalSize
                    vec2 uv = localUV / decalSize;

                    // Zu Textur-Koordinaten mappen
                    uv = uv * 0.5 + 0.5;

                    uv.y = 1.0 - uv.y;

                    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
                        return vec4(0.0, 0.0, 0.0, 0.0);
                    }

                    return texture2D(tex, uv);
                }

                // Gestrichelte Outline für aktive Grafik-Layer
                vec4 getDashedOutline(vec2 meshUV, vec2 decalCenterUV, vec2 decalSize, float rot, float isEditing) {
                    if (isEditing < 0.5) {
                        return vec4(0.0);
                    }

                    vec2 relativeUV = meshUV - decalCenterUV;
                    vec2 localUV = rotate2D(rot) * relativeUV;
                    vec2 uv = localUV / decalSize;

                    // Normalisierte Koordinaten (-1 bis 1)
                    vec2 normUV = uv;

                    // Outline-Breite in UV-Space (anpassbar)
                    float outlineWidth = 0.04;
                    float innerWidth = 0.02;

                    // Prüfe ob wir am Rand sind
                    float distToEdgeX = min(abs(normUV.x - 1.0), abs(normUV.x + 1.0));
                    float distToEdgeY = min(abs(normUV.y - 1.0), abs(normUV.y + 1.0));
                    float distToEdge = min(distToEdgeX, distToEdgeY);

                    // Nur innerhalb des Decal-Bereichs zeichnen (mit etwas Toleranz nach außen)
                    bool insideX = normUV.x >= -1.0 - outlineWidth && normUV.x <= 1.0 + outlineWidth;
                    bool insideY = normUV.y >= -1.0 - outlineWidth && normUV.y <= 1.0 + outlineWidth;

                    if (!insideX || !insideY) {
                        return vec4(0.0);
                    }

                    // Prüfe ob wir im Outline-Bereich sind
                    bool onHorizontalEdge = (abs(normUV.y - 1.0) < outlineWidth || abs(normUV.y + 1.0) < outlineWidth)
                                            && normUV.x >= -1.0 && normUV.x <= 1.0;
                    bool onVerticalEdge = (abs(normUV.x - 1.0) < outlineWidth || abs(normUV.x + 1.0) < outlineWidth)
                                          && normUV.y >= -1.0 && normUV.y <= 1.0;

                    if (!onHorizontalEdge && !onVerticalEdge) {
                        return vec4(0.0);
                    }

                    // Gestricheltes Muster basierend auf Position entlang der Kante
                    float dashFrequency = 8.0;   // Weniger Wiederholungen = längere Segmente
                    float dashRatio = 0.6;       // 60% Strich, 40% Lücke

                    float edgePosition;
                    if (onHorizontalEdge) {
                        edgePosition = normUV.x;
                    } else {
                        edgePosition = normUV.y;
                    }

                    float dashPattern = fract(edgePosition * dashFrequency);

                    if (dashPattern > dashRatio) {
                        return vec4(0.0); // Lücke
                    }

                    // Gelbe Outline-Farbe (#FFDE00)
                    vec3 outlineColor = vec3(1.0, 0.87, 0.0);

                    // Weichere Kanten für die Outline
                    float alpha = 1.0;
                    float edgeSoftness = 0.01;

                    if (onHorizontalEdge) {
                        float distFromEdge = min(abs(normUV.y - 1.0), abs(normUV.y + 1.0));
                        alpha = 1.0 - smoothstep(innerWidth, outlineWidth, distFromEdge);
                    } else {
                        float distFromEdge = min(abs(normUV.x - 1.0), abs(normUV.x + 1.0));
                        alpha = 1.0 - smoothstep(innerWidth, outlineWidth, distFromEdge);
                    }

                    return vec4(outlineColor, alpha);
                }
                `
        );

        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <map_fragment>',
            `
                #include <map_fragment>

                // Sammelt die maximale Decal-Deckung (Alpha) an diesem Fragment,
                // um auf Glas optional volle Deckkraft erzwingen zu können.
                float decalAlphaCoverage = 0.0;

                vec4 decalColor;
                if (isGroupDecal > 0.5) {
                    decalColor = uvBasedDecalProjection(
                        decalMap,
                        vMeshUV,
                        decalCenterUV,
                        decalSizeUV,
                        decalRotation
                    );
                } else {
                    decalColor = planarProjection(
                        decalMap,
                        vWorldPosition,
                        decalCenter,
                        decalSize,
                        decalNormal,
                        decalRotation
                    );
                }

                diffuseColor.rgb = mix(diffuseColor.rgb, decalColor.rgb, decalColor.a);
                decalAlphaCoverage = max(decalAlphaCoverage, decalColor.a);

                if (hasSideDecal > 0.5) {
                    vec4 sideDecalColor = planarProjection(
                        sideDecalMap,
                        vWorldPosition,
                        sideDecalCenter,
                        sideDecalSize,
                        sideDecalNormal,
                        sideDecalRotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, sideDecalColor.rgb, sideDecalColor.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, sideDecalColor.a);
                }

                // Render alle 8 Grafik-Layers (Layer 0 zuerst, dann Layer 1, etc.)
                // So liegt die zuletzt hochgeladene Grafik oben
                if (graphicLayer0Active > 0.5) {
                    vec4 layer0Color = uvBasedDecalProjection(
                        graphicLayer0Map, vMeshUV, graphicLayer0CenterUV, graphicLayer0SizeUV, graphicLayer0Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer0Color.rgb, layer0Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer0Color.a);
                }
                if (graphicLayer1Active > 0.5) {
                    vec4 layer1Color = uvBasedDecalProjection(
                        graphicLayer1Map, vMeshUV, graphicLayer1CenterUV, graphicLayer1SizeUV, graphicLayer1Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer1Color.rgb, layer1Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer1Color.a);
                }
                if (graphicLayer2Active > 0.5) {
                    vec4 layer2Color = uvBasedDecalProjection(
                        graphicLayer2Map, vMeshUV, graphicLayer2CenterUV, graphicLayer2SizeUV, graphicLayer2Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer2Color.rgb, layer2Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer2Color.a);
                }
                if (graphicLayer3Active > 0.5) {
                    vec4 layer3Color = uvBasedDecalProjection(
                        graphicLayer3Map, vMeshUV, graphicLayer3CenterUV, graphicLayer3SizeUV, graphicLayer3Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer3Color.rgb, layer3Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer3Color.a);
                }
                if (graphicLayer4Active > 0.5) {
                    vec4 layer4Color = uvBasedDecalProjection(
                        graphicLayer4Map, vMeshUV, graphicLayer4CenterUV, graphicLayer4SizeUV, graphicLayer4Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer4Color.rgb, layer4Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer4Color.a);
                }
                if (graphicLayer5Active > 0.5) {
                    vec4 layer5Color = uvBasedDecalProjection(
                        graphicLayer5Map, vMeshUV, graphicLayer5CenterUV, graphicLayer5SizeUV, graphicLayer5Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer5Color.rgb, layer5Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer5Color.a);
                }
                if (graphicLayer6Active > 0.5) {
                    vec4 layer6Color = uvBasedDecalProjection(
                        graphicLayer6Map, vMeshUV, graphicLayer6CenterUV, graphicLayer6SizeUV, graphicLayer6Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer6Color.rgb, layer6Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer6Color.a);
                }
                if (graphicLayer7Active > 0.5) {
                    vec4 layer7Color = uvBasedDecalProjection(
                        graphicLayer7Map, vMeshUV, graphicLayer7CenterUV, graphicLayer7SizeUV, graphicLayer7Rotation
                    );
                    diffuseColor.rgb = mix(diffuseColor.rgb, layer7Color.rgb, layer7Color.a);
                    decalAlphaCoverage = max(decalAlphaCoverage, layer7Color.a);
                }

                // Gestrichelte Outlines für aktive (editing) Layer zeichnen
                vec4 outline0 = getDashedOutline(vMeshUV, graphicLayer0CenterUV, graphicLayer0SizeUV, graphicLayer0Rotation, graphicLayer0Editing);
                vec4 outline1 = getDashedOutline(vMeshUV, graphicLayer1CenterUV, graphicLayer1SizeUV, graphicLayer1Rotation, graphicLayer1Editing);
                vec4 outline2 = getDashedOutline(vMeshUV, graphicLayer2CenterUV, graphicLayer2SizeUV, graphicLayer2Rotation, graphicLayer2Editing);
                vec4 outline3 = getDashedOutline(vMeshUV, graphicLayer3CenterUV, graphicLayer3SizeUV, graphicLayer3Rotation, graphicLayer3Editing);
                vec4 outline4 = getDashedOutline(vMeshUV, graphicLayer4CenterUV, graphicLayer4SizeUV, graphicLayer4Rotation, graphicLayer4Editing);
                vec4 outline5 = getDashedOutline(vMeshUV, graphicLayer5CenterUV, graphicLayer5SizeUV, graphicLayer5Rotation, graphicLayer5Editing);
                vec4 outline6 = getDashedOutline(vMeshUV, graphicLayer6CenterUV, graphicLayer6SizeUV, graphicLayer6Rotation, graphicLayer6Editing);
                vec4 outline7 = getDashedOutline(vMeshUV, graphicLayer7CenterUV, graphicLayer7SizeUV, graphicLayer7Rotation, graphicLayer7Editing);

                // Alle Outlines überlagern
                diffuseColor.rgb = mix(diffuseColor.rgb, outline0.rgb, outline0.a);
                diffuseColor.rgb = mix(diffuseColor.rgb, outline1.rgb, outline1.a);
                diffuseColor.rgb = mix(diffuseColor.rgb, outline2.rgb, outline2.a);
                diffuseColor.rgb = mix(diffuseColor.rgb, outline3.rgb, outline3.a);
                diffuseColor.rgb = mix(diffuseColor.rgb, outline4.rgb, outline4.a);
                diffuseColor.rgb = mix(diffuseColor.rgb, outline5.rgb, outline5.a);
                diffuseColor.rgb = mix(diffuseColor.rgb, outline6.rgb, outline6.a);
                diffuseColor.rgb = mix(diffuseColor.rgb, outline7.rgb, outline7.a);

                // CMS-Setting "Decals auf Glas voll deckend": Im Decal-Bereich volle Deckkraft
                // erzwingen. Das Glas selbst (außerhalb des Decals) behält seine Transparenz,
                // da nur dort interpoliert wird, wo eine Decal-Deckung vorliegt.
                if (uOpaqueDecal > 0.5) {
                    diffuseColor.a = mix(diffuseColor.a, 1.0, clamp(decalAlphaCoverage, 0.0, 1.0));
                }
                `
        );
    };

    material.needsUpdate = true;
    return material;
}

// ===== TRIPLANAR DECAL CREATION =====
export function createTriplanarDecal(groupName, sideName, pos, norm, tex, rotZ = 0, scaleF = 1) {
    if (!tex || !norm || !pos) return null;

    const meshes = sideMeshes.get(groupName)?.get(sideName);
    if (!meshes || !meshes.size) return null;

    const img = tex.image;
    const aspectRatio = img ? img.width / img.height : 1;

    const baseSize = 2.4;

    const center = pos.clone();
    const size = new THREE.Vector3(
        baseSize * aspectRatio * scaleF,
        baseSize * scaleF,
        baseSize * scaleF
    );

    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;

    const sideDecalData = {
        texture: tex,
        center: center,
        size: size,
        rotation: rotZ,
        normal: norm.clone().normalize(),
    };

    const key = `${groupName}:${sideName}`;
    if (!decalMaterialsByGroup.has(key)) {
        decalMaterialsByGroup.set(key, new Map());
    }
    const originalMatsMap = decalMaterialsByGroup.get(key);

    meshes.forEach(mesh => {
        const hasGroupDecal = mesh.material &&
            (Array.isArray(mesh.material) ?
                mesh.material[0]?.userData?.decalUniforms?.isGroupDecal?.value === 1.0 :
                mesh.material?.userData?.decalUniforms?.isGroupDecal?.value === 1.0);

        if (hasGroupDecal) {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach(mat => {
                    if (mat.userData.decalUniforms) {
                        mat.userData.decalUniforms.hasSideDecal.value = 1.0;
                        mat.userData.decalUniforms.sideDecalMap.value = tex;
                        mat.userData.decalUniforms.sideDecalCenter.value = center;
                        mat.userData.decalUniforms.sideDecalSize.value = size;
                        mat.userData.decalUniforms.sideDecalRotation.value = rotZ;
                        mat.userData.decalUniforms.sideDecalNormal.value = norm.clone().normalize();

                        if (mat.userData.shader) {
                            mat.userData.shader.uniforms.hasSideDecal.value = 1.0;
                            mat.userData.shader.uniforms.sideDecalMap.value = tex;
                            mat.userData.shader.uniforms.sideDecalCenter.value = center;
                            mat.userData.shader.uniforms.sideDecalSize.value = size;
                            mat.userData.shader.uniforms.sideDecalRotation.value = rotZ;
                            mat.userData.shader.uniforms.sideDecalNormal.value = norm.clone().normalize();
                        }
                        mat.needsUpdate = true;
                    }
                });
            } else {
                if (mesh.material.userData.decalUniforms) {
                    mesh.material.userData.decalUniforms.hasSideDecal.value = 1.0;
                    mesh.material.userData.decalUniforms.sideDecalMap.value = tex;
                    mesh.material.userData.decalUniforms.sideDecalCenter.value = center;
                    mesh.material.userData.decalUniforms.sideDecalSize.value = size;
                    mesh.material.userData.decalUniforms.sideDecalRotation.value = rotZ;
                    mesh.material.userData.decalUniforms.sideDecalNormal.value = norm.clone().normalize();

                    if (mesh.material.userData.shader) {
                        mesh.material.userData.shader.uniforms.hasSideDecal.value = 1.0;
                        mesh.material.userData.shader.uniforms.sideDecalMap.value = tex;
                        mesh.material.userData.shader.uniforms.sideDecalCenter.value = center;
                        mesh.material.userData.shader.uniforms.sideDecalSize.value = size;
                        mesh.material.userData.shader.uniforms.sideDecalRotation.value = rotZ;
                        mesh.material.userData.shader.uniforms.sideDecalNormal.value = norm.clone().normalize();
                    }
                    mesh.material.needsUpdate = true;
                }
            }
        } else {
            if (!originalMatsMap.has(mesh)) {
                originalMatsMap.set(mesh, Array.isArray(mesh.material) ? [...mesh.material] : mesh.material);
            }

            const projectionData = {
                center,
                size,
                rotation: rotZ,
                scale: scaleF,
                normal: norm.clone().normalize(),
                isGroupDecal: false,
            };

            if (Array.isArray(mesh.material)) {
                mesh.material = mesh.material.map(mat =>
                    createTriplanarDecalMaterial(mat, tex, projectionData, null, graphicLayers, groupName, sideName)
                );
            } else {
                mesh.material = createTriplanarDecalMaterial(mesh.material, tex, projectionData, null, graphicLayers, groupName, sideName);
            }
        }
    });

    return {
        userData: {
            lastHit: {point: pos.clone(), normal: norm.clone()},
            group: groupName,
            side: sideName,
            sideDecalData,
        },
        material: {map: tex},
        dispose: () => {
            removeTriplanarDecal(groupName, sideName);
        }
    };
}

// ===== TRIPLANAR DECAL REMOVAL =====
export function removeTriplanarDecal(groupName, sideName) {
    const key = `${groupName}:${sideName}`;
    const meshes = sideMeshes.get(groupName)?.get(sideName);
    if (!meshes || !meshes.size) return;

    meshes.forEach(mesh => {
        const hasGroupDecal = mesh.material &&
            (Array.isArray(mesh.material) ?
                mesh.material[0]?.userData?.decalUniforms?.isGroupDecal?.value === 1.0 :
                mesh.material?.userData?.decalUniforms?.isGroupDecal?.value === 1.0);

        if (hasGroupDecal) {
            if (Array.isArray(mesh.material)) {
                mesh.material.forEach(mat => {
                    if (mat.userData.decalUniforms) {
                        mat.userData.decalUniforms.hasSideDecal.value = 0.0;
                        if (mat.userData.shader) {
                            mat.userData.shader.uniforms.hasSideDecal.value = 0.0;
                        }
                        mat.needsUpdate = true;
                    }
                });
            } else {
                if (mesh.material.userData.decalUniforms) {
                    mesh.material.userData.decalUniforms.hasSideDecal.value = 0.0;
                    if (mesh.material.userData.shader) {
                        mesh.material.userData.shader.uniforms.hasSideDecal.value = 0.0;
                    }
                    mesh.material.needsUpdate = true;
                }
            }
        } else {
            const originalMatsMap = decalMaterialsByGroup.get(key);
            if (originalMatsMap && originalMatsMap.has(mesh)) {
                const originalMat = originalMatsMap.get(mesh);

                if (Array.isArray(mesh.material)) {
                    mesh.material.forEach(mat => {
                        if (mat && mat.dispose) mat.dispose();
                    });
                } else if (mesh.material && mesh.material.dispose) {
                    mesh.material.dispose();
                }

                mesh.material = originalMat;
            }
        }
    });

    const originalMatsMap = decalMaterialsByGroup.get(key);
    if (originalMatsMap) {
        originalMatsMap.clear();
        decalMaterialsByGroup.delete(key);
    }
}

// ===== GROUP TRIPLANAR DECAL CREATION =====
export function createGroupTriplanarDecal(groupName, pos, norm, tex, rotZ = 0, scaleF = 1, hitUV = null) {
    if (!tex || !norm || !pos) return null;

    const isGraphicId = groupName.startsWith && groupName.startsWith('graphic-');
    const allGroupMeshes = new Set();

    if (isGraphicId) {
        // Für Grafiken: Alle Meshes sammeln
        sideMeshes.forEach((groupSides) => {
            groupSides.forEach(meshSet => {
                meshSet.forEach(mesh => allGroupMeshes.add(mesh));
            });
        });
    } else {
        // Für Gruppen: Nur Meshes dieser Gruppe
        const groupSides = sideMeshes.get(groupName);
        if (!groupSides || !groupSides.size) return null;

        groupSides.forEach(meshSet => {
            meshSet.forEach(mesh => allGroupMeshes.add(mesh));
        });
    }

    if (!allGroupMeshes.size) return null;

    const img = tex.image;
    const aspectRatio = img ? img.width / img.height : 1;
    const baseSizeUV = 0.5;

    const centerUV = hitUV ? new THREE.Vector2(hitUV.x, hitUV.y) : new THREE.Vector2(0.5, 0.5);

    const sizeUV = new THREE.Vector2(
        baseSizeUV * aspectRatio * scaleF,
        baseSizeUV * scaleF
    );

    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;

    const projectionData = {
        centerUV,
        sizeUV,
        rotation: rotZ,
        scale: scaleF,
        isGroupDecal: true,
    };

    const key = `group:${groupName}`;
    if (!decalMaterialsByGroup.has(key)) {
        decalMaterialsByGroup.set(key, new Map());
    }
    const originalMatsMap = decalMaterialsByGroup.get(key);

    allGroupMeshes.forEach(mesh => {
        let existingSideDecalData = null;

        const currentMat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
        if (currentMat?.userData?.decalUniforms) {
            const uniforms = currentMat.userData.decalUniforms;

            if (uniforms.isGroupDecal?.value === 0.0) {
                existingSideDecalData = {
                    texture: uniforms.decalMap.value,
                    center: uniforms.decalCenter.value.clone(),
                    size: uniforms.decalSize.value.clone(),
                    rotation: uniforms.decalRotation.value,
                    normal: uniforms.decalNormal.value.clone(),
                };
            }
        }

        if (!originalMatsMap.has(mesh)) {
            originalMatsMap.set(mesh, Array.isArray(mesh.material) ? [...mesh.material] : mesh.material);
        }

        if (Array.isArray(mesh.material)) {
            mesh.material = mesh.material.map(mat =>
                createTriplanarDecalMaterial(mat, tex, projectionData, existingSideDecalData, graphicLayers, groupName, null)
            );
        } else {
            mesh.material = createTriplanarDecalMaterial(mesh.material, tex, projectionData, existingSideDecalData, graphicLayers, groupName, null);
        }
    });

    return {
        userData: {
            lastHit: {point: pos.clone(), normal: norm.clone(), uv: centerUV},
            group: groupName,
            centerUV,
            sizeUV,
            projectionData,
            isGroupDecal: true,
        },
        material: {map: tex},
        dispose: () => {
            removeGroupTriplanarDecal(groupName);
        }
    };
}

// ===== GROUP TRIPLANAR DECAL REMOVAL =====
export function removeGroupTriplanarDecal(groupName) {
    const key = `group:${groupName}`;
    const originalMatsMap = decalMaterialsByGroup.get(key);
    if (!originalMatsMap) return;

    originalMatsMap.forEach((originalMat, mesh) => {
        if (Array.isArray(mesh.material)) {
            mesh.material.forEach(mat => {
                if (mat && mat.dispose) mat.dispose();
            });
        } else if (mesh.material && mesh.material.dispose) {
            mesh.material.dispose();
        }

        mesh.material = originalMat;
    });

    originalMatsMap.clear();
    decalMaterialsByGroup.delete(key);
}

// ===== GROUP TRIPLANAR DECAL UPDATE =====
export function updateGroupTriplanarDecal(groupName, rotation, scale) {
    const isGraphicId = groupName.startsWith && groupName.startsWith('graphic-');
    const allGroupMeshes = new Set();

    if (isGraphicId) {
        // Für Grafiken: Alle Meshes sammeln
        sideMeshes.forEach((groupSides) => {
            groupSides.forEach(meshSet => {
                meshSet.forEach(mesh => allGroupMeshes.add(mesh));
            });
        });
    } else {
        // Für Gruppen: Nur Meshes dieser Gruppe
        const groupSides = sideMeshes.get(groupName);
        if (!groupSides) return;

        groupSides.forEach(meshSet => {
            meshSet.forEach(mesh => allGroupMeshes.add(mesh));
        });
    }

    if (!allGroupMeshes.size) return;

    const decalObj = groupDecals.get(groupName);
    const baseSizeUV = 0.5;
    const img = decalObj?.material?.map?.image;
    const aspectRatio = img ? img.width / img.height : 1;

    const newSizeUV = new THREE.Vector2(
        baseSizeUV * aspectRatio * scale,
        baseSizeUV * scale
    );

    allGroupMeshes.forEach(mesh => {
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

        materials.forEach(mat => {
            if (mat && mat.userData.decalUniforms) {
                mat.userData.decalUniforms.decalRotation.value = rotation;
                mat.userData.decalUniforms.decalSizeUV.value.copy(newSizeUV);
                mat.userData.decalUniforms.decalScale.value = scale;

                if (mat.userData.shader) {
                    mat.userData.shader.uniforms.decalRotation.value = rotation;
                    mat.userData.shader.uniforms.decalSizeUV.value.copy(newSizeUV);
                    mat.userData.shader.uniforms.decalScale.value = scale;
                }
            }
        });
    });

    requestRender();
}

// ===== GROUP DECAL REBUILD =====
export function rebuildGroupDecal(groupName) {
    const old = groupDecals.get(groupName);
    const settings = groupDecalSettings.get(groupName);
    if (!old || !settings) return;

    const {rotation, scale} = settings;

    if (old.userData && old.userData.group && old.userData.isGroupDecal) {
        updateGroupTriplanarDecal(
            old.userData.group,
            THREE.MathUtils.degToRad(rotation),
            scale
        );
        return;
    }
}

// ===== TRIPLANAR DECAL UPDATE =====
export function updateTriplanarDecal(groupName, sideName, rotation, scale) {
    const meshes = sideMeshes.get(groupName)?.get(sideName);
    if (!meshes) return;

    meshes.forEach(mesh => {
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

        materials.forEach(mat => {
            if (mat && mat.userData.decalUniforms) {
                mat.userData.decalUniforms.decalRotation.value = rotation;
                const baseSize = 2.4;
                const img = mat.userData.decalUniforms.decalMap.value.image;
                const aspectRatio = img ? img.width / img.height : 1;
                const newSize = new THREE.Vector3(
                    baseSize * aspectRatio * scale,
                    baseSize * scale,
                    baseSize * scale
                );
                mat.userData.decalUniforms.decalSize.value.copy(newSize);
                mat.userData.decalUniforms.decalScale.value = scale;

                if (mat.userData.shader) {
                    mat.userData.shader.uniforms.decalRotation.value = rotation;
                    mat.userData.shader.uniforms.decalSize.value.copy(newSize);
                    mat.userData.shader.uniforms.decalScale.value = scale;
                }
            }
        });
    });

    requestRender();
}

// ===== DECAL HINT MANAGEMENT =====
export function showDecalHint(containerEl) {
    const decalHintShown = window._decalHintShown;
    if (decalHintShown) return;
    window._decalHintShown = true;
    const hintShownTime = Date.now();
    window._hintShownTime = hintShownTime;

    const hintOverlayEl = document.createElement('div');
    hintOverlayEl.id = 'decalHintOverlay';
    hintOverlayEl.innerHTML = `
        <div>
            <b>${GLOBALS.tc.place_graphic || 'Grafik platzieren'}:</b><br>
            ${GLOBALS.tc.click_to_place || 'Klicken Sie auf das Modell, um die Grafik zu platzieren'}<br>
            ${GLOBALS.tc.click_to_reposition || 'Erneut klicken, um die Grafik neu zu positionieren'}
        </div>
    `;

    const viewer = document.getElementById('viewer') || document.body;
    viewer.appendChild(hintOverlayEl);
    window._hintOverlayEl = hintOverlayEl;

    requestAnimationFrame(() => hintOverlayEl.style.opacity = 1);
}

export function hideDecalHint() {
    const hintOverlayEl = window._hintOverlayEl;
    if (!hintOverlayEl) return;

    // Mindestens 5 Sekunden anzeigen
    const elapsed = Date.now() - (window._hintShownTime || 0);
    const minDisplayTime = 5000; // 5 Sekunden

    if (elapsed < minDisplayTime) {
        // Noch nicht lange genug angezeigt, später erneut versuchen
        setTimeout(hideDecalHint, minDisplayTime - elapsed);
        return;
    }

    hintOverlayEl.style.opacity = 0;
    setTimeout(() => {
        if (window._hintOverlayEl) {
            window._hintOverlayEl.remove();
            window._hintOverlayEl = null;
        }
    }, 300);
}
